import { create } from 'zustand';
import { StreakResult } from '../services/streakService';

interface CacheEntry {
  result: StreakResult;
  fetchedAt: number;
  /**
   * The dateID (YYYYMMDD) of the most recent logged day at the time this
   * result was computed. Used by the incremental re-verification path so we
   * know whether today/yesterday still form an unbroken chain.
   */
  lastLoggedDateID: string;
}

interface StreakState {
  cache: Map<string, CacheEntry>; // key = userId
  loadingUsers: Set<string>;
  errorUsers: Map<string, string>;

  setStreakData:    (userId: string, result: StreakResult, lastLoggedDateID: string) => void;
  setLoading:       (userId: string, loading: boolean) => void;
  setError:         (userId: string, error: string | null) => void;
  clearCache:       (userId: string) => void;

  getStreakResult:      (userId: string) => StreakResult | null;
  getLastLoggedDateID:  (userId: string) => string | null;
  isCacheValid:         (userId: string, maxAge?: number) => boolean;
  isLoading:            (userId: string) => boolean;
  getError:             (userId: string) => string | null;
}

const CACHE_TTL = 3_600_000; // 1 hour

export const useStreakStore = create<StreakState>((set, get) => ({
  cache:        new Map(),
  loadingUsers: new Set(),
  errorUsers:   new Map(),

  setStreakData: (userId, result, lastLoggedDateID) =>
    set((state) => {
      const nextCache    = new Map(state.cache);
      const nextLoading  = new Set(state.loadingUsers);
      const nextErrors   = new Map(state.errorUsers);
      nextCache.set(userId, { result, fetchedAt: Date.now(), lastLoggedDateID });
      nextLoading.delete(userId);
      nextErrors.delete(userId);
      return { cache: nextCache, loadingUsers: nextLoading, errorUsers: nextErrors };
    }),

  setLoading: (userId, loading) =>
    set((state) => {
      const next = new Set(state.loadingUsers);
      loading ? next.add(userId) : next.delete(userId);
      return { loadingUsers: next };
    }),

  setError: (userId, error) =>
    set((state) => {
      const nextErrors  = new Map(state.errorUsers);
      const nextLoading = new Set(state.loadingUsers);
      if (error) nextErrors.set(userId, error);
      else nextErrors.delete(userId);
      nextLoading.delete(userId);
      return { errorUsers: nextErrors, loadingUsers: nextLoading };
    }),

  clearCache: (userId) =>
    set((state) => {
      const next = new Map(state.cache);
      next.delete(userId);
      return { cache: next };
    }),

  getStreakResult:     (userId) => get().cache.get(userId)?.result ?? null,
  getLastLoggedDateID: (userId) => get().cache.get(userId)?.lastLoggedDateID ?? null,

  isCacheValid: (userId, maxAge = CACHE_TTL) => {
    const entry = get().cache.get(userId);
    if (!entry) return false;
    if (Date.now() - entry.fetchedAt > maxAge) return false;
    return true;
  },

  isLoading: (userId) => get().loadingUsers.has(userId),

  getError: (userId) => get().errorUsers.get(userId) ?? null,
}));
